import { useState, useEffect } from 'react';
import { ShieldCheck, Info, AlertTriangle, X, BarChart3, TrendingUp } from 'lucide-react';
import { getUserTrackRecordBadge } from '../services/api';

/**
 * Renders the verified track record badge for any user.
 * Two display modes:
 *   - inline: tiny chip ("✓ Verified" or "Unverified") for marketplace listing rows
 *   - full: larger card with stats popover for detail pages
 *
 * Use `trackRecord` prop if pre-loaded (saves a fetch). Otherwise fetches by userId.
 */
export default function TrackRecordBadge({ userId, trackRecord, mode = 'inline', showStatsLink = true }) {
  const [data, setData] = useState(trackRecord || null);
  const [loading, setLoading] = useState(!trackRecord && !!userId);
  const [showPopover, setShowPopover] = useState(false);

  useEffect(() => {
    if (trackRecord) { setData(trackRecord); return; }
    if (!userId) return;
    setLoading(true);
    getUserTrackRecordBadge(userId)
      .then(res => setData(res?.data || res))
      .catch(err => console.error('Track record badge fetch failed', err))
      .finally(() => setLoading(false));
  }, [userId, trackRecord]);

  if (loading) return <span className="inline-block h-5 w-20 bg-dark-700 rounded animate-pulse" />;
  if (!data) return null;

  const verified = !!data.verified;

  if (mode === 'inline') {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setShowPopover(p => !p); }}
        className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border whitespace-nowrap transition-colors ${
          verified
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20'
            : 'bg-amber-500/5 border-amber-500/20 text-amber-500/70 hover:bg-amber-500/15'
        }`}
        title={verified ? 'Verified track record — click for stats' : data.reason || 'Unverified'}
      >
        {verified ? <ShieldCheck className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
        {verified ? 'Verified' : 'Unverified'}
        {showPopover && (
          <TrackRecordPopover data={data} onClose={() => setShowPopover(false)} />
        )}
      </button>
    );
  }

  // mode === 'full'
  if (!verified) {
    return (
      <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-amber-400 text-xs font-bold">Unverified seller</p>
          <p className="text-xs text-gray-400 mt-0.5">{data.reason || 'No verified track record on file.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
      <div className="flex items-start gap-3 mb-3">
        <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-emerald-400 text-sm font-bold">Verified Track Record</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {data.summary?.roundTrips} round-trips over {data.windowDays} days · {data.exchangeCount} exchange{data.exchangeCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {data.summary && (
        <div className="grid grid-cols-3 gap-2 text-xs">
          <Metric label="Net P&L" value={`${data.summary.netPnl >= 0 ? '+' : ''}$${formatNum(data.summary.netPnl, 0)}`} accent={data.summary.netPnl > 0 ? 'emerald' : 'red'} />
          <Metric label="Win rate" value={`${formatNum(data.summary.winRate, 1)}%`} />
          <Metric label="Max DD" value={`${formatNum(data.summary.maxDrawdownPct, 1)}%`} accent="red" />
        </div>
      )}

      <p className="text-[10px] text-gray-600 mt-3 leading-relaxed">
        Verified means haythix observed these trades on the seller's connected exchange. It does not constitute a recommendation. Past performance does not predict future results.
      </p>
    </div>
  );
}

function TrackRecordPopover({ data, onClose }) {
  return (
    <div
      className="absolute z-50 top-full left-0 mt-2 w-72 bg-dark-800 border border-dark-600 rounded-xl shadow-2xl p-4 text-left"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-2">
        <p className="text-emerald-400 text-xs font-bold flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" /> Verified Track Record
        </p>
        <button onClick={onClose} className="text-gray-500 hover:text-white">
          <X className="w-3 h-3" />
        </button>
      </div>
      {data.summary && (
        <div className="grid grid-cols-2 gap-2 text-xs mb-2">
          <Metric label="Net P&L" value={`${data.summary.netPnl >= 0 ? '+' : ''}$${formatNum(data.summary.netPnl, 0)}`} accent={data.summary.netPnl > 0 ? 'emerald' : 'red'} />
          <Metric label="Win rate" value={`${formatNum(data.summary.winRate, 1)}%`} />
          <Metric label="Round-trips" value={data.summary.roundTrips} />
          <Metric label="Max DD" value={`${formatNum(data.summary.maxDrawdownPct, 1)}%`} accent="red" />
        </div>
      )}
      <p className="text-[10px] text-gray-500 mb-2">
        {data.windowDays}-day window · {data.exchangeCount} exchange{data.exchangeCount !== 1 ? 's' : ''}
      </p>
      <p className="text-[9px] text-gray-600 leading-relaxed">
        Past performance ≠ future results. Verified means we saw these trades on the seller's connected exchange.
      </p>
    </div>
  );
}

function Metric({ label, value, accent }) {
  const colorMap = { emerald: 'text-emerald-400', red: 'text-red-400' };
  return (
    <div className="bg-dark-700/40 rounded p-2">
      <p className="text-[9px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`text-sm font-bold font-mono mt-0.5 ${colorMap[accent] || 'text-white'}`}>{value}</p>
    </div>
  );
}

function formatNum(v, dp = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}
